/**
 * Licences (`/licences`; #1111, #1112, #1119, #1122, #1123). Slice `web-licences-screen` (#1130) owns this screen;
 * `web-licences-rail` (#1129) owns `section.tsx` and `change-form.tsx` in this folder.
 *
 * Manager and billing only (the API's BillingActorDep). It replaces the interim claude.ai "Pax8 Reconciliation" page:
 * Licences, Usage (UBB), Renewals, Books to fix, Changes and Mapping, with notes per line. The URL owns the tab and
 * the Licences quick filter, so a view can be shared by its address.
 */
import { EmptyState, Tabs, TabsContent, TabsList, TabsTrigger, TooltipProvider } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import { Frame, PaneHeader } from '../../components/frame'
import { describeError } from '../../lib/api/client'
import type { SyncRunOut } from '../../lib/api/generated'
import { useMe } from '../auth/session'
import { timeLabel } from '../profit/format'
import { BooksFixTab } from './books-fix'
import { ChangesTab } from './changes'
import { canSeeLicences, reconQuery } from './common'
import { LICENCE_FILTERS, type LicenceFilter, LicencesTab } from './licences-tab'
import { MappingTab } from './mapping'
import { RenewalsTab } from './renewals'
import { UsageTab } from './usage-tab'

export { canSeeLicences } from './common'

export const LICENCE_TABS = ['licences', 'usage', 'renewals', 'books-fix', 'changes', 'mapping'] as const
export type LicenceTab = (typeof LICENCE_TABS)[number]

const TAB_LABEL: Record<LicenceTab, string> = {
  licences: 'Licences',
  usage: 'Usage (UBB)',
  renewals: 'Renewals',
  'books-fix': 'Books to fix',
  changes: 'Changes',
  mapping: 'Mapping',
}

export interface LicencesSearch {
  tab?: LicenceTab
  filter?: LicenceFilter
}

const FILTER_IDS = LICENCE_FILTERS.map((f) => f.id) as readonly string[]

/** validateSearch for `/licences`: anything unknown is dropped; defaults are omitted. */
export function parseLicencesSearch(raw: Record<string, unknown>): LicencesSearch {
  const out: LicencesSearch = {}
  if (typeof raw.tab === 'string' && (LICENCE_TABS as readonly string[]).includes(raw.tab) && raw.tab !== 'licences')
    out.tab = raw.tab as LicenceTab
  if (typeof raw.filter === 'string' && FILTER_IDS.includes(raw.filter) && raw.filter !== 'action') out.filter = raw.filter as LicenceFilter
  return out
}

const RUN_LABEL: Record<string, string> = {
  pax8_sync: 'Pax8',
  pax8_invoices: 'Pax8 invoices',
  books_recurring_lines: 'Books recurring invoices',
}

/** When the data was read, and which reads failed (a failed read is unknown, never zero). */
function Freshness() {
  const q = useQuery(reconQuery())
  // The tabs carry the full ErrorBanner for this read; the header says the freshness is unknown, never blank.
  if (q.isError && !q.data) {
    return (
      <p className="text-xs text-warning" role="status" data-slot="licences-freshness-unreachable">
        Freshness unknown: the reconciliation is unreachable ({describeError(q.error)}).
      </p>
    )
  }
  if (!q.data) return null
  const runs: SyncRunOut[] = q.data.runs ?? []
  const failed = runs.filter((r) => r.state !== 'ok' && r.state !== 'running')
  return (
    <div className="space-y-1 text-xs text-muted-foreground">
      <p>
        {q.data.computed_at ? `Reconciled ${timeLabel(q.data.computed_at)}` : 'Not reconciled yet'}
        {runs.length
          ? ` · ${runs.map((r) => `${RUN_LABEL[r.kind] ?? r.kind} ${r.state === 'ok' ? timeLabel(r.finished_at ?? r.started_at) : r.state}`).join(' · ')}`
          : ''}
      </p>
      {failed.length ? (
        <p className="text-warning" role="status">
          {failed.map((r) => RUN_LABEL[r.kind] ?? r.kind).join(', ')} did not finish on the last run: figures that depend on it are unknown,
          not zero.
        </p>
      ) : null}
    </div>
  )
}

export function LicencesScreen({ search, onSearch }: { search: LicencesSearch; onSearch: (patch: Partial<LicencesSearch>) => void }) {
  const me = useMe()
  const tab: LicenceTab = search.tab ?? 'licences'
  if (me && !canSeeLicences(me.role)) {
    return (
      <Frame
        active="licences"
        railless
        main={<EmptyState title="Licences are for managers and billing." detail="Your role cannot open this screen." />}
      />
    )
  }
  return (
    <Frame
      active="licences"
      railless
      main={
        <TooltipProvider>
          <div className="flex h-full flex-col overflow-y-auto" data-scroll-top>
            <PaneHeader
              title="Licences"
              subtitle="Every licence and usage charge Pax8 bills us, against what Ewee Inc bills the customer in Books. Pax8 is the source of truth for which licence and how many."
            />
            <div className="px-5 pt-2">
              <Freshness />
            </div>
            <Tabs value={tab} onValueChange={(value) => onSearch({ tab: value === 'licences' ? undefined : (value as LicenceTab) })}>
              <TabsList className="px-5" aria-label="Licences views">
                {LICENCE_TABS.map((t) => (
                  <TabsTrigger key={t} value={t}>
                    {TAB_LABEL[t]}
                  </TabsTrigger>
                ))}
              </TabsList>
              <div className="px-5 py-4">
                <TabsContent value="licences">
                  {tab === 'licences' ? (
                    <LicencesTab
                      filter={search.filter ?? 'action'}
                      onFilter={(f) => onSearch({ filter: f === 'action' ? undefined : f })}
                    />
                  ) : null}
                </TabsContent>
                <TabsContent value="usage">{tab === 'usage' ? <UsageTab /> : null}</TabsContent>
                <TabsContent value="renewals">{tab === 'renewals' ? <RenewalsTab /> : null}</TabsContent>
                <TabsContent value="books-fix">{tab === 'books-fix' ? <BooksFixTab /> : null}</TabsContent>
                <TabsContent value="changes">{tab === 'changes' ? <ChangesTab /> : null}</TabsContent>
                <TabsContent value="mapping">{tab === 'mapping' ? <MappingTab /> : null}</TabsContent>
              </div>
            </Tabs>
          </div>
        </TooltipProvider>
      }
    />
  )
}
